/** Copyright (c) 2026 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { Box, Button, CloseButton, Divider, Flex, Input, SimpleGrid, Text } from '@chakra-ui/react';
import { UserDateComponent } from '@karya/core';
import { ParsedParams } from '@karya/generic-task-react';
import { useEffect, useRef, useState } from 'react';
import { ButtonVariant } from '@/themes/ButtonVariants';
import { ComponentRendererProps } from '../ComponentRenderer';
import {
  dateSubmitError,
  initialDateValue,
  initialOutputToWrite,
  isYearSelected,
  listYears,
  yearBounds,
  yearRangeLabel,
} from './dateValue';

const errorColor = '#DE3730';

export function UserDateRenderer(props: ComponentRendererProps) {
  const typedParams = props.node.params as ParsedParams<UserDateComponent>;

  if (!props.isCurrent) {
    const saved = initialDateValue(typedParams.outputDate, typedParams.preFill);
    return (
      <Text fontSize="sm" width="100%" wordBreak="break-word" whiteSpace="pre-wrap">
        {saved}
      </Text>
    );
  }

  return <CurrentDateInput {...props} />;
}

function CurrentDateInput(props: ComponentRendererProps) {
  const { isCurrent, isDocked, writeOutput, setSubmitHandler, node } = props;
  const typedParams = node.params as ParsedParams<UserDateComponent>;
  const component = node.component as UserDateComponent;
  const [text, setText] = useState(() => initialDateValue(typedParams.outputDate, typedParams.preFill));
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showYearPicker, setShowYearPicker] = useState(false);
  const textRef = useRef(text);
  textRef.current = text;
  const didWritePrefill = useRef(false);

  const bounds = yearBounds(typedParams.startRange, typedParams.endRange, new Date().getFullYear());
  const years = bounds ? listYears(bounds.start, bounds.end) : null;

  useEffect(() => {
    if (!isCurrent || !isDocked || didWritePrefill.current) {
      return;
    }
    didWritePrefill.current = true;
    const initial = initialOutputToWrite(typedParams.outputDate, typedParams.preFill);
    if (initial != null) {
      writeOutput(component.outputDate, initial);
    }
  }, [component.outputDate, isCurrent, isDocked, writeOutput, typedParams.outputDate, typedParams.preFill]);

  useEffect(() => {
    if (!isCurrent || !isDocked || !setSubmitHandler) {
      return;
    }

    setSubmitHandler((goNext) => {
      const error = dateSubmitError(textRef.current);
      if (error) {
        setErrorMessage(error);
        return;
      }
      setErrorMessage(null);
      goNext();
    });

    return () => {
      setSubmitHandler((goNext) => {
        goNext();
      });
    };
  }, [isCurrent, isDocked, setSubmitHandler]);

  const writeText = (nextText: string) => {
    setText(nextText);
    setErrorMessage(null);
    if (isCurrent) {
      writeOutput(component.outputDate, nextText);
    }
  };

  const selectYear = (year: number) => {
    writeText(String(year));
    setShowYearPicker(false);
  };

  const rangeLabel = yearRangeLabel(typedParams.startRange, typedParams.endRange);

  return (
    <Box width="100%" maxWidth="100%" minWidth={0} bg="white" borderRadius="8px" p="8px" overflow="hidden">
      {errorMessage && (
        <Text fontSize="xs" color={errorColor} wordBreak="break-word" mb="6px">
          {errorMessage}
        </Text>
      )}
      <Input
        aria-label="Date"
        size="sm"
        width="100%"
        bg="white"
        value={text}
        placeholder="Enter date"
        borderColor={errorMessage ? errorColor : undefined}
        autoComplete="off"
        spellCheck={false}
        onChange={(event) => writeText(event.target.value)}
      />
      {bounds == null && (
        <Text fontSize="xs" color={errorColor} mt="6px" wordBreak="break-word">
          Invalid year range
        </Text>
      )}
      {rangeLabel && bounds != null && (
        <Text fontSize="xs" color="gray.600" mt="6px" wordBreak="break-word">
          {rangeLabel}
        </Text>
      )}
      {years != null && (
        <>
          <Button
            mt="8px"
            size="sm"
            width="100%"
            variant={ButtonVariant.secondary_outlined}
            onClick={() => setShowYearPicker((open) => !open)}
          >
            Select year
          </Button>
          {showYearPicker && (
            <Box mt="8px" borderWidth="1px" borderColor="gray.200" borderRadius="8px" overflow="hidden">
              <Flex alignItems="center" px="8px" py="4px">
                <Text fontSize="sm" fontWeight="medium" flex="1">
                  Select Year
                </Text>
                <CloseButton size="sm" aria-label="Close year picker" onClick={() => setShowYearPicker(false)} />
              </Flex>
              <Divider />
              <SimpleGrid columns={3} spacing="6px" maxH="160px" overflowY="auto" p="8px">
                {years.map((year) => (
                  <Button
                    key={year}
                    size="xs"
                    variant={isYearSelected(text, year) ? ButtonVariant.secondary : ButtonVariant.secondary_outlined}
                    onClick={() => selectYear(year)}
                  >
                    {year}
                  </Button>
                ))}
              </SimpleGrid>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
